import axios from "axios";
import { authConfig } from "@/lib/auth";
import { getInstallId } from "@/lib/installationId";
import {
  decryptReflectionResponse,
  encryptReflectionPayload,
  reflectionEncryptionAvailable,
} from "@/lib/reflectionCrypto";

const REFLECTION_ENDPOINT = (process.env.REACT_APP_REFLECTION_ENDPOINT || "").trim();
const SUPABASE_KEY = authConfig.publishableKey;

export const reflectionCompanionAvailable = Boolean(
  REFLECTION_ENDPOINT && SUPABASE_KEY && reflectionEncryptionAvailable
);

const requestHeaders = () => ({
  "Content-Type": "application/json",
  apikey: SUPABASE_KEY,
});

/**
 * Ask the reflection companion.
 * Reflection content is envelope-encrypted before it reaches the gateway.
 * No account token or email is sent to the AI endpoint; abuse protection uses
 * only the encrypted random installation identifier plus a keyed IP hash on
 * the server when network address information is available.
 * @param {{text: string, mode: 'journal'|'zen'|'talk', protectList?: string[], companion?: string|null, history?: Array<{role:string,text:string}>}} args
 * @returns {Promise<{response: string}>}
 */
export const askReflection = async ({ text, mode, protectList, companion = null, history = [] }) => {
  if (!reflectionCompanionAvailable) {
    const error = new Error("Reflection companion backend is not configured");
    error.code = "REFLECTION_BACKEND_UNAVAILABLE";
    error.userMessage = "The reflection companion isn’t connected in this build yet.";
    throw error;
  }

  try {
    const { envelope, aesKey } = await encryptReflectionPayload({
      text,
      mode,
      protect_list: protectList || null,
      companion,
      history,
      install_id: getInstallId(),
    });

    const { data } = await axios.post(REFLECTION_ENDPOINT, envelope, {
      timeout: 45000,
      headers: requestHeaders(),
    });

    const decrypted = await decryptReflectionResponse(data, aesKey);
    if (!decrypted?.response || typeof decrypted.response !== "string") {
      const error = new Error("Reflection backend returned an invalid response");
      error.userMessage = "The reflection companion couldn’t answer just now.";
      throw error;
    }

    return { response: decrypted.response };
  } catch (error) {
    if (error?.response?.status === 429) {
      error.userMessage =
        "The reflection companion has reached a temporary usage limit. Your writing stays here; you can try again later.";
    } else if (error?.response?.status === 401 || error?.response?.status === 403) {
      error.userMessage = "The reflection companion couldn’t verify this app session just now.";
    } else if (!error?.userMessage) {
      error.userMessage = error?.response?.data?.error || "The reflection companion couldn’t answer just now.";
    }
    throw error;
  }
};
